import posthog from 'posthog-js';

const canTrack = () => (
  process.env.NODE_ENV !== 'development'
  && process.env.NODE_ENV !== 'test'
  && posthog
  && typeof posthog.capture === 'function'
);

export const trackReferralActionClicked = ({
  action,
  placement = 'unknown',
  selectedCount = 0,
  selectedAll = false,
  isArchivedView = false,
}) => {
  if (!canTrack()) return;

  posthog.capture('referral_action_clicked', {
    action,
    placement,
    selectedCount,
    selectedAll,
    isArchivedView,
  });
};

// Auto Split tracking

export const trackAutoSplitBannerShown = ({
  sendToken,
  totalPages = 0,
  partsCount = 0,
}) => {
  if (!canTrack()) return;

  posthog.capture('auto_split_banner_shown', {
    sendToken,
    totalPages,
    partsCount,
  });
};

export const trackAutoSplitBannerReviewClicked = ({
  sendToken,
  totalPages = 0,
  partsCount = 0,
}) => {
  if (!canTrack()) return;

  posthog.capture('auto_split_banner_review_clicked', {
    sendToken,
    totalPages,
    partsCount,
  });
};

export const trackAutoSplitBannerDismissed = ({
  sendToken,
}) => {
  if (!canTrack()) return;

  posthog.capture('auto_split_banner_dismissed', {
    sendToken,
  });
};

export const trackAutoSplitModalClosed = ({
  sendToken,
  totalPages = 0,
  partsCount = 0,
  selectedCount = 0,
  hadEdits = false,
}) => {
  if (!canTrack()) return;

  posthog.capture('auto_split_modal_closed', {
    sendToken,
    totalPages,
    partsCount,
    selectedCount,
    hadEdits,
  });
};

export const trackAutoSplitExecuted = ({
  sendToken,
  totalPages = 0,
  partsCount = 0,
  selectedCount = 0,
  splitMode,
  hadEdits = false,
}) => {
  if (!canTrack()) return;

  posthog.capture('auto_split_executed', {
    sendToken,
    totalPages,
    partsCount,
    selectedCount,
    splitMode,
    hadEdits,
  });
};

export const trackAutoSplitSucceeded = ({
  sendToken,
  totalPages = 0,
  partsCount = 0,
  selectedCount = 0,
  splitMode,
  hadEdits = false,
}) => {
  if (!canTrack()) return;

  posthog.capture('auto_split_succeeded', {
    sendToken,
    totalPages,
    partsCount,
    selectedCount,
    splitMode,
    hadEdits,
  });
};

export const trackAutoSplitFailed = ({
  sendToken,
  totalPages = 0,
  partsCount = 0,
  selectedCount = 0,
  splitMode,
  hadEdits = false,
  error,
}) => {
  if (!canTrack()) return;

  posthog.capture('auto_split_failed', {
    sendToken,
    totalPages,
    partsCount,
    selectedCount,
    splitMode,
    hadEdits,
    error,
  });
};

export const trackAutoSplitPartAdded = ({
  sendToken,
  totalPages = 0,
  partsCount = 0,
}) => {
  if (!canTrack()) return;

  posthog.capture('auto_split_part_added', {
    sendToken,
    totalPages,
    partsCount,
  });
};

export const trackAutoSplitPartRemoved = ({
  sendToken,
  totalPages = 0,
  partsCount = 0,
  removedIndex = 0,
}) => {
  if (!canTrack()) return;

  posthog.capture('auto_split_part_removed', {
    sendToken,
    totalPages,
    partsCount,
    removedIndex,
  });
};

export const trackReferralFilterChanged = ({
  filterKey,
  operation,
  source = 'unknown',
  valueCount = 0,
  hasValue = false,
}) => {
  if (!canTrack()) return;

  posthog.capture('referral_filter_changed', {
    filterKey,
    operation,
    source,
    valueCount,
    hasValue,
  });
};

export const trackInboxFilterChanged = ({
  filterKey,
  operation,
  source = 'unknown',
  valueCount = 0,
  hasValue = false,
}) => {
  if (!canTrack()) return;

  posthog.capture('inbox_filter_changed', {
    filterKey,
    operation,
    source,
    valueCount,
    hasValue,
  });
};

// Referral template management tracking

export const trackReferralTemplateEvent = ({
  action,
  ...props
}) => {
  if (!canTrack()) return;

  posthog.capture('referral_template_event', {
    action,
    ...props,
  });
};

// Recapture upsell tracking

const recaptureActivationFailureType = (error = '') => {
  const message = String(error || '').toLowerCase();

  if (message.includes('invalid tier')) return 'invalid_tier';
  if (message.includes('only administrators')) return 'admin_required';
  if (message.includes('not available')) return 'upsell_unavailable';
  if (message.includes('already activated')) return 'already_active';
  if (message.includes('activation failed')) return 'request_failed';

  return 'unknown';
};

export const trackRecaptureUpsellViewed = ({
  hasProductAccess = false,
  isAdmin = false,
}) => {
  if (!canTrack()) return;

  posthog.capture('recapture_upsell_viewed', {
    hasProductAccess,
    isAdmin,
  });
};

export const trackRecaptureUpsellCtaClicked = ({
  cta,
  placement = 'unknown',
  tier = null,
}) => {
  if (!canTrack()) return;

  posthog.capture('recapture_upsell_cta_clicked', {
    cta,
    placement,
    tier,
  });
};

export const trackRecaptureUpsellActivationSubmitted = ({ tier }) => {
  if (!canTrack()) return;

  posthog.capture('recapture_upsell_activation_submitted', { tier });
};

export const trackRecaptureUpsellActivationSucceeded = ({ tier }) => {
  if (!canTrack()) return;

  posthog.capture('recapture_upsell_activation_succeeded', { tier });
};

export const trackRecaptureUpsellActivationFailed = ({ tier, error }) => {
  if (!canTrack()) return;

  posthog.capture('recapture_upsell_activation_failed', {
    tier,
    errorType: recaptureActivationFailureType(error),
  });
};

export const trackRecaptureUpsellFlashClicked = ({ placement = 'nav' } = {}) => {
  if (!canTrack()) return;

  posthog.capture('recapture_upsell_flash_clicked', { placement });
};

export const trackRecaptureUpsellFlashDismissed = ({ placement = 'nav' } = {}) => {
  if (!canTrack()) return;

  posthog.capture('recapture_upsell_flash_dismissed', { placement });
};
